import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Chip, Button, Stack } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { intl } from "dmsbasereact";
import { useDmsAnalyticsContext } from "./DmsAnalyticsContext";
import { DEFAULT_MONTHS, CHANNEL_OPTIONS, CURRENT_YEAR, ALL_YEARS_VALUE } from "./constants";

const getMonthLabel = (monthValue) => {
    const month = DEFAULT_MONTHS.find((m) => m.value === monthValue);
    return month ? intl.get(month.labelKey).d(month.fallback) : "";
};

const getChannelLabel = (channelValue) => {
    const labels = {
        [CHANNEL_OPTIONS.ALL]: intl.get("DmsAnalytics.Channel.All").d("Todos"),
        [CHANNEL_OPTIONS.PUBLIC]: intl.get("DmsAnalytics.Channel.Public").d("Público"),
        [CHANNEL_OPTIONS.PRIVATE]: intl.get("DmsAnalytics.Channel.Private").d("Privado"),
    };
    return labels[channelValue] || "";
};

// Cross-filter type labels
const getCrossFilterTypeLabel = (type) => {
    const labels = {
        product: intl.get("DmsAnalytics.Filter.Product").d("Produto"),
        client: intl.get("DmsAnalytics.Filter.Client").d("Cliente"),
        batch: intl.get("DmsAnalytics.Filter.Batch").d("Lote"),
        uf: intl.get("DmsAnalytics.Filter.State").d("UF"),
    };
    return labels[type] || type;
};

/**
 * DmsAnalyticsChips - Displays active filter chips with ability to remove them
 * Shows channel, period (year/month), and cross-filter chips.
 * Highlights "All Time" selection with warning color.
 *
 * @param {Object} props - Component props
 * @param {Object} props.sx - MUI sx styling props
 */
export const DmsAnalyticsChips = ({ showApplyButton = true, sx, ...props }) => {
    const {
        filters,
        crossFilters,
        clearFilter,
        clearCrossFilterType,
        clearAllFilters,
        hasCrossFilters,
        hasPendingChanges,
        hasAppliedCrossFilters,
        applyFilters,
    } = useDmsAnalyticsContext();

    const chips = useMemo(() => {
        const result = [];

        const isChannelFiltered = filters.channel !== CHANNEL_OPTIONS.ALL;
        const isAllYears = filters.years.length === 1 && filters.years[0] === ALL_YEARS_VALUE;
        const isDefaultYear = filters.years.length === 1 && filters.years[0] === CURRENT_YEAR;
        const isYearsFiltered = !isDefaultYear;
        const isPeriodFiltered = filters.startMonth !== 1 || filters.endMonth !== 12;

        if (isChannelFiltered) {
            result.push({
                key: "channel",
                label: `${intl.get("DmsAnalytics.Filter.Channel").d("Canal")}: ${getChannelLabel(filters.channel)}`,
                onDelete: () => clearFilter("channel"),
            });
        }

        if (isYearsFiltered || isPeriodFiltered) {
            const monthRange =
                filters.startMonth === filters.endMonth
                    ? getMonthLabel(filters.startMonth)
                    : `${getMonthLabel(filters.startMonth)}-${getMonthLabel(filters.endMonth)}`;

            // Build years label
            let yearsLabel;
            if (isAllYears) {
                yearsLabel = intl.get("DmsAnalytics.Filter.AllYears").d("Todo o Período");
            } else if (filters.years.length === 1) {
                yearsLabel = `${filters.years[0]} (${monthRange})`;
            } else {
                // Multiple years selected - show sorted list
                const sortedYears = [...filters.years].sort((a, b) => b - a);
                yearsLabel = sortedYears.join(", ");
            }

            result.push({
                key: "period",
                label: `${intl.get("DmsAnalytics.Filter.Period").d("Período")}: ${yearsLabel}`,
                onDelete: () => {
                    clearFilter("period");
                    clearFilter("years");
                },
                color: isAllYears || filters.years.length > 5 ? "warning" : "default",
            });
        }

        // Cross-filters: one chip per type with all values (using pending/selected filters)
        Object.entries(crossFilters || {}).forEach(([type, items]) => {
            if (items && items.length > 0) {
                const typeLabel = getCrossFilterTypeLabel(type);
                const valuesLabel = items.map((i) => i.label).join(", ");
                // Truncate if too long
                const displayLabel = valuesLabel.length > 30
                    ? `${valuesLabel.substring(0, 27)}...`
                    : valuesLabel;

                result.push({
                    key: `cross_${type}`,
                    label: `${typeLabel}: ${displayLabel}`,
                    title: `${typeLabel}: ${valuesLabel}`, // Full tooltip
                    onDelete: () => clearCrossFilterType(type),
                    color: "primary",
                });
            }
        });

        return result;
    }, [filters, crossFilters, clearFilter, clearCrossFilterType]);

    // Show component if there are any filters (channel/period) OR any cross-filters (pending or applied)
    const hasAnyFilters = chips.length > 0 || hasCrossFilters || hasAppliedCrossFilters;

    if (!hasAnyFilters) {
        return null;
    }

    return (
        <Box sx={{ px: 2, py: 0.5, ...sx }} {...props}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                {chips.map((chip) => (
                    <Chip
                        key={chip.key}
                        label={chip.label}
                        title={chip.title || chip.label}
                        size="small"
                        variant="outlined"
                        color={chip.color || "default"}
                        onDelete={chip.onDelete}
                        sx={{ mb: 0.5 }}
                    />
                ))}

                {/* Apply Button - shows when there are pending changes */}
                {showApplyButton && hasPendingChanges && (
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={applyFilters}
                        startIcon={<PlayArrowIcon sx={{ fontSize: "14px !important" }} />}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            px: 1.5,
                            py: 0.4,
                        }}
                    >
                        {intl.get("DmsAnalytics.Action.ApplyFilters").d("Aplicar Filtros")}
                    </Button>
                )}

                {/* Applied indicator - shows when filters are applied and no pending changes */}
                {!hasPendingChanges && hasAppliedCrossFilters && (
                    <Chip
                        icon={<CheckIcon />}
                        label={intl.get("DmsAnalytics.Status.FiltersApplied").d("Filtros Aplicados")}
                        size="small"
                        color="success"
                        variant="filled"
                        sx={{ mb: 0.5 }}
                    />
                )}

                {/* Clear all button */}
                {(hasAppliedCrossFilters || chips.length > 0) && (
                    <Button
                        size="small"
                        onClick={clearAllFilters}
                        sx={{
                            textTransform: "none",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: "#d32f2f",
                            backgroundColor: "rgba(211, 47, 47, 0.08)",
                            border: "1px solid rgba(211, 47, 47, 0.2)",
                            borderRadius: "6px",
                            opacity: 0.75,
                            px: 1,
                            py: 0.15,
                            minHeight: 0,
                            lineHeight: 1.4,
                            "&:hover": {
                                opacity: 1,
                                backgroundColor: "rgba(211, 47, 47, 0.15)",
                                border: "1px solid rgba(211, 47, 47, 0.35)",
                            },
                        }}
                    >
                        {intl.get("DmsAnalytics.Action.ClearFilters").d("Limpar Filtros")}
                    </Button>
                )}
            </Stack>
        </Box>
    );
};

DmsAnalyticsChips.propTypes = {
    showApplyButton: PropTypes.bool,
    sx: PropTypes.object,
};
